/**
 * IntegrationsPage - Admin panel for managing platform integrations
 * Shows available, coming soon, and configured integrations
 * PHASE: POWERBI-ENABLEMENT-1 - Added API Key Management and Documentation
 */

import React, { useState, useEffect } from 'react';
import { 
  Link, Settings, Check, X, ExternalLink, Bell, 
  Cloud, MessageSquare, FileText, Database, Users,
  Mail, Calendar, Lock, AlertCircle, Briefcase, Phone,
  Key, Copy, Trash2, Eye, EyeOff, BookOpen, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { toast } from 'sonner';

const IntegrationsPage = () => {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [showNotifyModal, setShowNotifyModal] = useState(null);
  
  // POWERBI-ENABLEMENT-1: API Key Management State
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [showApiDocsModal, setShowApiDocsModal] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [apiDocs, setApiDocs] = useState(null);
  const [apiDocsLoading, setApiDocsLoading] = useState(false);

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    setLoading(true);
    try {
      const res = await api.getIntegrations().catch(() => ({ data: null }));
      if (res.data?.integrations) {
        setIntegrations(res.data.integrations);
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // POWERBI-ENABLEMENT-1: Load API Keys
  const loadApiKeys = async () => {
    setApiKeysLoading(true);
    try {
      const res = await api.get('/api/tenant/api-keys');
      setApiKeys(res.data || []);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setApiKeysLoading(false);
    }
  };
  
  // POWERBI-ENABLEMENT-1: Generate API Key
  const handleGenerateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    
    try {
      const res = await api.post('/api/tenant/api-keys', {
        name: newKeyName.trim(),
        description: newKeyDescription.trim() || null
      });
      
      setCreatedKey(res.data);
      setNewKeyName('');
      setNewKeyDescription('');
      loadApiKeys();
      toast.success('API key created successfully');
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    }
  };
  
  // POWERBI-ENABLEMENT-1: Revoke API Key
  const handleRevokeKey = async (keyId, keyName) => {
    if (!window.confirm(`Are you sure you want to revoke the API key "${keyName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/api/tenant/api-keys/${keyId}`);
      toast.success('API key revoked');
      loadApiKeys();
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      toast.error('Failed to revoke API key');
    }
  };
  
  // POWERBI-ENABLEMENT-1: Copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };
  
  // POWERBI-ENABLEMENT-1: Load API Documentation
  const loadApiDocs = async () => {
    setApiDocsLoading(true);
    try {
      const res = await api.get('/api/developer/docs');
      setApiDocs(res.data);
    } catch (error) {
      console.error('Failed to load API docs:', error);
      toast.error('Failed to load API documentation');
    } finally {
      setApiDocsLoading(false);
    }
  };
  
  // POWERBI-ENABLEMENT-1: Open API Key Modal
  const openApiKeyModal = () => {
    setShowApiKeyModal(true);
    setCreatedKey(null);
    loadApiKeys();
  };
  
  // POWERBI-ENABLEMENT-1: Open API Docs Modal
  const openApiDocsModal = () => {
    setShowApiDocsModal(true);
    loadApiDocs();
  };

  const handleNotifyMe = async (integrationKey) => {
    if (!notifyEmail) {
      toast.error('Please enter your email');
      return;
    }
    try {
      await api.subscribeIntegrationNotify(integrationKey, notifyEmail).catch(() => {});
      toast.success("We'll notify you when this integration is available!");
      setShowNotifyModal(null);
      setNotifyEmail('');
    } catch (error) {
      toast.error('Failed to subscribe');
    }
  };

  // All integrations (static list with statuses)
  const allIntegrations = [
    // Available Integrations
    {
      key: 'sso_saml',
      name: 'SSO (SAML 2.0)',
      description: 'Single Sign-On with SAML 2.0 providers like Okta, Azure AD, OneLogin',
      icon: Lock,
      category: 'Security',
      status: 'available',
      tier: 'enterprise'
    },
    {
      key: 'sso_oidc',
      name: 'SSO (OpenID Connect)',
      description: 'Single Sign-On with OIDC providers like Auth0, Google Workspace',
      icon: Lock,
      category: 'Security',
      status: 'available',
      tier: 'enterprise'
    },
    {
      key: 'email_notifications',
      name: 'Email Notifications',
      description: 'Send notifications and alerts via email (SMTP or SendGrid)',
      icon: Mail,
      category: 'Notifications',
      status: 'available',
      tier: 'starter',
      configPath: '/notifications'
    },
    {
      key: 'whatsapp',
      name: 'WhatsApp Business',
      description: 'Send Daily Signal™ notifications via WhatsApp Business API',
      icon: Phone,
      category: 'Notifications',
      status: 'available',
      tier: 'professional',
      configPath: '/notifications',
      requiresKeys: ['WHATSAPP_PHONE_NUMBER_ID', 'WHATSAPP_ACCESS_TOKEN']
    },
    {
      key: 'teams',
      name: 'Microsoft Teams',
      description: 'Post updates and Daily Signal™ alerts to Teams channels via webhooks or Graph API',
      icon: Users,
      category: 'Notifications',
      status: 'available',
      tier: 'professional',
      configPath: '/notifications'
    },
    
    // Available Integrations - Project Management
    {
      key: 'jira',
      name: 'Jira',
      description: 'Two-way sync with Atlassian Jira projects, epics, and issues',
      icon: Database,
      category: 'Project Management',
      status: 'available',
      tier: 'enterprise',
      configPath: '/settings/jira'
    },
    {
      key: 'azure_devops',
      name: 'Azure DevOps',
      description: 'Two-way sync with Azure DevOps work items and boards',
      icon: Cloud,
      category: 'Project Management',
      status: 'available',
      tier: 'enterprise',
      configPath: '/settings/integrations/azure-devops'
    },
    {
      key: 'zendesk',
      name: 'Zendesk',
      description: 'Create tickets and track service requests from ScopeOps',
      icon: MessageSquare,
      category: 'Service Desk',
      status: 'coming_soon'
    },
    {
      key: 'slack',
      name: 'Slack',
      description: 'Send notifications and updates to Slack channels',
      icon: MessageSquare,
      category: 'Notifications',
      status: 'coming_soon'
    },
    {
      key: 'sharepoint',
      name: 'SharePoint / OneDrive',
      description: 'Sync templates and documents with SharePoint or OneDrive',
      icon: FileText,
      category: 'Documents',
      status: 'coming_soon'
    },
    {
      key: 'google_drive',
      name: 'Google Drive',
      description: 'Store and sync proposal documents with Google Drive',
      icon: Cloud,
      category: 'Documents',
      status: 'coming_soon'
    },
    {
      key: 'docusign',
      name: 'DocuSign',
      description: 'Electronic signatures for proposal approvals',
      icon: FileText,
      category: 'E-Signature',
      status: 'coming_soon'
    },
    {
      key: 'calendar_sync',
      name: 'Calendar Sync',
      description: 'Sync milestones and deadlines with Google/Outlook calendars',
      icon: Calendar,
      category: 'Productivity',
      status: 'coming_soon'
    },
    {
      key: 'salesforce',
      name: 'Salesforce',
      description: 'Create opportunities from converted proposals',
      icon: Briefcase,
      category: 'CRM',
      status: 'coming_soon'
    },
    {
      key: 'hubspot',
      name: 'HubSpot',
      description: 'Track proposals in your HubSpot deal pipeline',
      icon: Briefcase,
      category: 'CRM',
      status: 'coming_soon'
    }
  ];

  const categories = [...new Set(allIntegrations.map(i => i.category))];

  const getStatusBadge = (status, tier) => {
    if (status === 'available') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
          Available
        </span>
      );
    }
    if (status === 'configured') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
          <Check className="w-3 h-3" /> Configured
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
        Coming Soon
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
          <Link className="w-7 h-7 text-teal-500" />
          Integrations
        </h1>
        <p className="text-[var(--clr-text-secondary)] mt-1">
          Connect ScopeOps with your favorite tools and services
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-teal-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-teal-900">Integration Roadmap</h3>
            <p className="text-sm text-teal-700 mt-1">
              We are actively building integrations with popular tools. Click Notify Me on any upcoming 
              integration to be the first to know when it is available.
            </p>
          </div>
        </div>
      </div>

      {/* Integrations by Category */}
      {categories.map((category) => {
        const categoryIntegrations = allIntegrations.filter(i => i.category === category);
        
        return (
          <div key={category} className="space-y-4">
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">{category}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryIntegrations.map((integration) => {
                const IntegrationIcon = integration.icon;
                const isComingSoon = integration.status === 'coming_soon';
                
                return (
                  <div 
                    key={integration.key}
                    className={`bg-white rounded-lg border p-5 ${
                      isComingSoon ? 'opacity-75' : 'hover:shadow-md'
                    } transition-all`}
                    data-testid={`integration-${integration.key}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2.5 rounded-lg ${
                        isComingSoon ? 'bg-gray-100' : 'bg-teal-100'
                      }`}>
                        <IntegrationIcon className={`w-5 h-5 ${
                          isComingSoon ? 'text-gray-500' : 'text-teal-600'
                        }`} />
                      </div>
                      {getStatusBadge(integration.status, integration.tier)}
                    </div>
                    
                    <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                    <p className="text-sm text-gray-500 mt-1 mb-4">{integration.description}</p>
                    
                    {integration.tier && (
                      <div className="mb-3">
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                          {integration.tier.charAt(0).toUpperCase() + integration.tier.slice(1)} tier
                        </span>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      {isComingSoon ? (
                        <button
                          onClick={() => setShowNotifyModal(integration.key)}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors"
                        >
                          <Bell className="w-4 h-4" />
                          Notify Me
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => integration.configPath ? navigate(integration.configPath) : null}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors"
                          >
                            <Settings className="w-4 h-4" />
                            Configure
                          </button>
                          <button
                            className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* API Access Section */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-gray-100 rounded-lg">
            <Settings className="w-6 h-6 text-gray-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">API Access</h3>
            <p className="text-sm text-gray-500 mt-1">
              Build custom integrations using our REST API. Available for Business and Enterprise tiers.
            </p>
            <div className="mt-4 flex gap-3">
              <button 
                onClick={openApiDocsModal}
                data-testid="view-api-docs-btn"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-2"
              >
                <BookOpen className="w-4 h-4" />
                View API Docs
              </button>
              <button 
                onClick={openApiKeyModal}
                data-testid="generate-api-key-btn"
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center gap-2"
              >
                <Key className="w-4 h-4" />
                Generate API Key
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notify Me Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowNotifyModal(null)}></div>
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 z-10">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Get Notified</h2>
            <p className="text-sm text-gray-500 mb-4">
              Enter your email to be notified when{' '}
              <strong>{allIntegrations.find(i => i.key === showNotifyModal)?.name}</strong>{' '}
              integration is available.
            </p>
            
            <input
              type="email"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setShowNotifyModal(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => handleNotifyMe(showNotifyModal)}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
              >
                Notify Me
              </button>
            </div>
          </div>
        </div>
      )}

      {/* POWERBI-ENABLEMENT-1: API Key Management Modal */}
      {showApiKeyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => { setShowApiKeyModal(false); setCreatedKey(null); }}></div>
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 z-10 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Key className="w-5 h-5 text-teal-600" />
                API Key Management
              </h2>
              <button 
                onClick={() => { setShowApiKeyModal(false); setCreatedKey(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Newly Created Key - Show Only Once */}
            {createdKey && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-800">Save Your API Key</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      This is the only time you will see this key. Copy it and store it securely.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-white border rounded font-mono text-sm text-gray-800 break-all">
                        {createdKey.api_key}
                      </code>
                      <button
                        onClick={() => copyToClipboard(createdKey.api_key)}
                        data-testid="copy-api-key-btn"
                        className="px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Create New Key Form */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
              <h3 className="font-medium text-gray-900 mb-3">Create New API Key</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Key name (e.g., Power BI Production)"
                  data-testid="api-key-name-input"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <input
                  type="text"
                  value={newKeyDescription}
                  onChange={(e) => setNewKeyDescription(e.target.value)}
                  placeholder="Description (optional)"
                  data-testid="api-key-description-input"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <button
                  onClick={handleGenerateApiKey}
                  data-testid="create-api-key-btn"
                  className="w-full px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center justify-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  Generate API Key
                </button>
              </div>
            </div>

            {/* Existing Keys List */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Active API Keys</h3>
              {apiKeysLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                </div>
              ) : apiKeys.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No API keys created yet</p>
              ) : (
                <div className="space-y-3">
                  {apiKeys.map((key) => (
                    <div 
                      key={key.id} 
                      className={`p-4 border rounded-lg ${key.status === 'revoked' ? 'bg-gray-50 opacity-60' : 'bg-white'}`}
                      data-testid={`api-key-${key.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{key.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              key.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {key.status}
                            </span>
                          </div>
                          <code className="text-xs text-gray-500 font-mono mt-1 block">{key.key_prefix}</code>
                          {key.description && (
                            <p className="text-xs text-gray-500 mt-1">{key.description}</p>
                          )}
                        </div>
                        {key.status === 'active' && (
                          <button
                            onClick={() => handleRevokeKey(key.id, key.name)}
                            data-testid={`revoke-key-${key.id}`}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Revoke key"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="flex gap-4 mt-2 text-xs text-gray-500">
                        <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                        {key.last_used_at && (
                          <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                        )}
                        <span>By: {key.created_by}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t text-sm text-gray-500">
              <p>API keys provide access to the ScopeOps REST API for Power BI and external integrations. Maximum 5 active keys per tenant.</p>
            </div>
          </div>
        </div>
      )}

      {/* POWERBI-ENABLEMENT-1: API Documentation Modal */}
      {showApiDocsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowApiDocsModal(false)}></div>
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl p-6 z-10 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-teal-600" />
                API Documentation
              </h2>
              <button 
                onClick={() => setShowApiDocsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {apiDocsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              </div>
            ) : apiDocs ? (
              <div className="space-y-8">
                {/* Quick Start Section */}
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                  <h3 className="font-semibold text-teal-900 mb-3 flex items-center gap-2">
                    🚀 {apiDocs.quick_start?.title || 'Quick Start'}
                  </h3>
                  <div className="space-y-3">
                    {apiDocs.quick_start?.steps?.map((step, idx) => (
                      <div key={idx} className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                          {step.step}
                        </span>
                        <div>
                          <span className="font-medium text-gray-900">{step.title}</span>
                          <p className="text-sm text-gray-600">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Authentication */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Authentication</h3>
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Type:</strong> {apiDocs.authentication?.type}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Header:</strong> <code className="px-2 py-0.5 bg-gray-200 rounded">{apiDocs.authentication?.header}</code>
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Format:</strong> <code className="px-2 py-0.5 bg-gray-200 rounded">{apiDocs.authentication?.format}</code>
                    </p>
                  </div>
                </div>

                {/* Endpoints */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Available Endpoints</h3>
                  <div className="space-y-4">
                    {apiDocs.endpoints && Object.entries(apiDocs.endpoints).map(([key, endpoint]) => (
                      <details key={key} className="border rounded-lg">
                        <summary className="px-4 py-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                              {endpoint.method}
                            </span>
                            <span className="font-medium text-gray-900">{endpoint.name}</span>
                          </div>
                          <code className="text-sm text-gray-500">{endpoint.endpoint}</code>
                        </summary>
                        <div className="px-4 py-3 border-t bg-gray-50">
                          <p className="text-sm text-gray-600 mb-3">{endpoint.description}</p>
                          
                          {endpoint.parameters && endpoint.parameters.length > 0 && (
                            <div className="mb-3">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Parameters</h4>
                              <div className="space-y-1">
                                {endpoint.parameters.map((param, idx) => (
                                  <div key={idx} className="text-xs">
                                    <code className="text-blue-600">{param.name}</code>
                                    <span className="text-gray-500"> ({param.type}) - {param.description}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="mb-3">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Request Example</h4>
                            <pre className="p-3 bg-gray-900 text-gray-100 rounded text-xs overflow-x-auto">
{`curl -X ${endpoint.method} "${endpoint.request_example?.url}"
  -H "X-API-Key: your_api_key_here"`}
                            </pre>
                          </div>
                          
                          {endpoint.response_example && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Response Example</h4>
                              <pre className="p-3 bg-gray-900 text-gray-100 rounded text-xs overflow-x-auto max-h-48">
                                {JSON.stringify(endpoint.response_example, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>

                {/* Power Query Examples */}
                {apiDocs.power_query_examples && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Power Query M Code</h3>
                    <details className="border rounded-lg">
                      <summary className="px-4 py-3 cursor-pointer hover:bg-gray-50">
                        Projects Query
                      </summary>
                      <div className="px-4 py-3 border-t bg-gray-50">
                        <pre className="p-3 bg-gray-900 text-gray-100 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                          {apiDocs.power_query_examples.projects}
                        </pre>
                      </div>
                    </details>
                  </div>
                )}

                {/* Rate Limits */}
                {apiDocs.rate_limits && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Rate Limits</h3>
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <div className="space-y-2">
                        {apiDocs.rate_limits.limits?.map((limit, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-600">{limit.endpoint}</span>
                            <span className="font-medium text-gray-900">{limit.limit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Failed to load documentation</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsPage;
